import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ActionListItem, ListResult, SeasonCounter, TodayResponse } from '@tww/shared';
import { fmtDate, fmtNum, todayIso, totalProgress } from '@tww/shared';
import { api, qs } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { ErrorState, KpiLine, Panel, Skeleton } from '../ui/basics';
import { Icon } from '../ui/Icon';
import { ActionProgress, ActionStatusPill, MissingText, fmtRange } from './actions/common';

/** „Fehlt noch“: offene Punkte mit Überfälligem als Unterzeile */
function MissingCell({ a }: { a: ActionListItem }) {
  if (a.missing.ok || a.status === 'offen' || a.status === 'ausgefallen') return <MissingText m={a.missing} />;
  const t = totalProgress(a.progress);
  return (
    <>
      <span className={t.late ? 'miss' : ''}>
        {t.open} {t.open === 1 ? 'Punkt' : 'Punkte'} offen
      </span>
      {t.late > 0 && <span className="sm miss">{t.late} überfällig</span>}
    </>
  );
}

/** Übersicht: KPI-Karten, Nächste Aktionen, Offene Punkte */
export function DashboardPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const q = useQuery({ queryKey: ['today', all], queryFn: () => api.get<TodayResponse>(`/dashboard/today${qs({ all: all ? 1 : undefined })}`) });
  const seasons = useQuery({ queryKey: ['action-seasons'], queryFn: () => api.get<{ items: SeasonCounter[] }>('/actions/seasons').then((r) => r.items), enabled: can('actions'), staleTime: 30_000 });
  const next = useQuery({ queryKey: ['actions', 'upcoming'], queryFn: () => api.get<ListResult<ActionListItem>>('/actions?filter[upcoming]=1&sort=date&order=asc&pageSize=8'), enabled: can('actions') });
  const dateLabel = new Date(`${todayIso()}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const current = seasons.data?.find((s) => s.current);
  const items = q.data?.items ?? [];
  const shown = showAll ? items : items.slice(0, 8);
  return (
    <>
      <div className="head">
        <div>
          <h1>Übersicht</h1>
          <div className="sub">{[dateLabel, current ? `${current.label} in Planung` : null].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="actions">
          {can('actions', 'write') && (
            <Link className="btn primary" to="/aktionen/neu">
              <Icon name="plus" /> Neue Aktion anlegen
            </Link>
          )}
          {can('contacts', 'write') && (
            <Link className="btn" to="/kontakte/neu">
              Neuer Kontakt
            </Link>
          )}
        </div>
      </div>
      {q.isLoading ? (
        <Skeleton />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => q.refetch()} />
      ) : (
        <>
          {q.data!.kpis.length > 0 && (
            <KpiLine items={q.data!.kpis.filter((k) => k.label !== 'Kontakte' || q.data!.kpis.length < 3)} />
          )}
          <div className="grid g21 dash">
            <Panel title="Nächste Aktionen" action={can('actions') && <Link className="btn sm" to="/aktionen">Alle Aktionen</Link>}>
              {!can('actions') ? (
                <div className="empty">Kein Zugriff auf Aktionen</div>
              ) : next.isLoading ? (
                <Skeleton rows={4} />
              ) : (next.data?.items.length ?? 0) === 0 ? (
                <div className="empty">Keine anstehenden Aktionen</div>
              ) : (
                <div className="tw">
                  <table className="dash-next">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th className="a">Aktion</th>
                        <th>Status</th>
                        <th>Fortschritt</th>
                        <th className="m">Fehlt noch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {next.data!.items.map((a) => (
                        <tr key={a.id} className="row" onClick={() => nav(`/aktionen/${a.id}`)}>
                          <td className={`d ${a.date ? '' : 'mut'}`}>{fmtRange(a)}</td>
                          <td className="a">
                            <Link className="link" to={`/aktionen/${a.id}`}>
                              {a.label}
                            </Link>
                            {Boolean(a.participants_planned || a.trees_planned) && <span className="sm">{[a.participants_planned ? `${a.participants_planned} Teilnehmende` : null, a.trees_planned ? `${fmtNum(a.trees_planned)} Bäume` : null].filter(Boolean).join(' · ')}</span>}
                          </td>
                          <td>
                            <ActionStatusPill status={a.status} />
                          </td>
                          <td>
                            <ActionProgress progress={a.progress} pct={false} />
                          </td>
                          <td className="m">
                            <MissingCell a={a} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
            <Panel
              title={<>Offene Punkte {items.length > 0 && <span className="mut" style={{ fontWeight: 400 }}>· {items.length}</span>}</>}
              action={
                <div className="seg" role="group" aria-label="Meine oder alle">
                  <button type="button" className={!all ? 'on' : ''} onClick={() => setAll(false)}>
                    Meine
                  </button>
                  <button type="button" className={all ? 'on' : ''} onClick={() => setAll(true)}>
                    Alle
                  </button>
                </div>
              }
              className="today"
            >
              <ul className="list">
                {items.length === 0 && <li className="mut">Nichts offen</li>}
                {shown.map((i) => (
                  <li key={i.key}>
                    <span className={`k tag-${i.kind.toLowerCase().replace(/[^a-z]/g, '')}`}>{i.kind}</span>
                    <span>
                      <Link className="link" to={i.href}>
                        {i.title}
                      </Link>
                      {i.due && <span className={`sm ${i.late ? 'miss' : 'mut'}`}> · {dueText(i)}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {items.length > 8 && (
                <div className="pb" style={{ paddingTop: 8 }}>
                  <button type="button" className="btn sm" onClick={() => setShowAll((s) => !s)}>
                    {showAll ? 'weniger anzeigen' : `alle ${items.length} anzeigen`}
                  </button>
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
    </>
  );
}

/** Datum eines Punkts; beim Kennzeichen „Überfällig“ nicht doppelt */
function dueText(i: TodayResponse['items'][number]): string {
  const d = fmtDate(i.due);
  if (i.kind === 'Abschluss') return d;
  if (!i.late) return `fällig ${d}`;
  return i.kind === 'Überfällig' ? `seit ${d}` : `überfällig seit ${d}`;
}
